import VideocamIcon from '@mui/icons-material/Videocam';
import {
  Button,
  Chip,
  Dialog,
  DialogContent,
  IconButton,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { HandleGenerateLinkParams, Strategy, VideoConfButtonProps } from './VideoConfButton.types';
import { pushSnackbar } from 'FRONTEND/actions/app';
import { generateRoomUrl } from 'FRONTEND/components/Calendar/utils';
import { joinConfRoom } from 'FRONTEND/reducers/rightPanelSlice';
import { APPOINTMENT_STATUS_CANCELLED, VIDEO_CONSULTATION } from 'FRONTEND/utils/constants';
import i18n from 'FRONTEND/utils/i18n';

const VideoConfButton = ({
  color,
  appointment,
  fullButton,
  hideIcon,
  rtcServerUrl,
} : VideoConfButtonProps) => {
  const dispatch = useDispatch();
  const isSmall = useSelector((state: { app: { isSmall: boolean } }) => state.app.isSmall);

  // Link menu management
  const userId = useSelector((state: { user: { id: string } }) => state.user.id);
  const isUserPatient = appointment.patient?.userId === userId; // User is the current appointment patient

  const [linkMenuAnchorEl, setLinkMenuAnchorEl] = useState<null | HTMLElement>(null);
  const handleButtonClick = async(event: React.MouseEvent<HTMLElement>) => {
    // User can only join as a patient, so no copying link needed.
    if (isUserPatient) {
      return handleGenerateLink({
        strategy: Strategy.OPEN,
      });
    }

    // User is a practitioner or secretary.
    if (appointment.appointmentType === VIDEO_CONSULTATION) {
      // For videoconsultation, show menu to choose link type.
      setLinkMenuAnchorEl(event.currentTarget);
    } else {
      // No menu, directly copy or open the link, depending on whether the user should participate in the appointment.
      await handleGenerateLink({
        strategy: appointment.requester?.id === userId || appointment.requiredMap[userId] ? Strategy.OPEN : Strategy.COPY,
      });
    }
  };
  const handleLinkMenuClose = () => {
    setLinkMenuAnchorEl(null);
  };

  // Room link management
  const handleGenerateLink = async({
    strategy = Strategy.COPY,
    isGuest = false,
    linkType = 'practitioner',
  }: HandleGenerateLinkParams = {}) => {
    let url = '';
    url = generateRoomUrl(rtcServerUrl, appointment.roomId, {
      isMedicalConsultation: appointment.appointmentType === VIDEO_CONSULTATION,
      enableGuestAccess: isGuest,
    });

    if (strategy === Strategy.OPEN) {
      dispatch(joinConfRoom({ videoConferenceUrl: url }));
    } else {
      // Avoiding i18n._('The ')
      let message;
      if (linkType === 'practitioner') {
        message = i18n._('The practitioner videoconference link was successfully copied to your clipboard.');
      } else {
        message = i18n._('The patient videoconference link was successfully copied to your clipboard.');
      }
      try {
        await navigator.clipboard.writeText(url);
        dispatch(pushSnackbar({ message, variant: 'success' }));
        handleLinkMenuClose();
      } catch (err) {
        setCopyableUrl(url);
      }
    }
  };

  // Copy dialog management
  const [copyableUrl, setCopyableUrl] = useState<string | null>(null);
  const handleCopyDialogClose = () => {
    setCopyableUrl(null);
    handleLinkMenuClose();
  };

  const videoButton = fullButton ? (
    <div>
      <Button
        fullWidth
        variant={color ? 'contained' : 'outlined'}
        onClick={handleButtonClick}
        startIcon={hideIcon ? undefined : <VideocamIcon />}
        color={color}
        disabled={appointment.status === APPOINTMENT_STATUS_CANCELLED}
      >
        {i18n._('Videoconference room')}
      </Button>
    </div>
  ) : (
    <Tooltip title={i18n._('Videoconference room')}>
      <IconButton
        aria-label='share'
        onClick={handleButtonClick}
        color={color}
        size='large'
        disabled={appointment.status === APPOINTMENT_STATUS_CANCELLED}
      >
        <VideocamIcon />
      </IconButton>
    </Tooltip>
  );

  return (
    <>
      {videoButton}
      <Menu
        open={Boolean(linkMenuAnchorEl)}
        anchorEl={linkMenuAnchorEl}
        onClose={handleLinkMenuClose}
        className='roomLink-copyModal'
      >
        {appointment.expertId === userId && (
          <MenuItem
            onClick={() => handleGenerateLink({ strategy: Strategy.OPEN })}
          >
            {i18n._('Join as practitioner')}
          </MenuItem>
        )}
        <MenuItem onClick={() => handleGenerateLink()}>
          {i18n._('Copy practitioner link')}
        </MenuItem>
        {appointment.expertId !== userId && [
          <MenuItem
            key='joinAsPatient'
            onClick={() => handleGenerateLink({
              isGuest: true,
              strategy: Strategy.OPEN,
            })}
          >
            {i18n._('Join as patient')}
          </MenuItem>,
          <MenuItem key='copyPatientLink' onClick={() => handleGenerateLink({ linkType: 'patient' })}>
            {i18n._('Copy patient link')}
          </MenuItem>,
        ]}
      </Menu>
      <Dialog
        fullScreen={isSmall}
        open={Boolean(copyableUrl)}
        onClose={handleCopyDialogClose}
      >
        <DialogContent>
          <Chip
            variant='outlined'
            label={<div className='appointmentPreview-url'>{copyableUrl}</div>}
          />
        </DialogContent>
      </Dialog>
    </>
  );
};

export default VideoConfButton;
